"use client";

import React from "react";
import {
  Grid,
  Box,
  Typography,
  Chip,
  Divider,
  Paper,
} from "@mui/material";

const InfoItem = ({ label, value, subValue, color }) => (
  <Box>
    <Typography variant="caption" sx={{ color: "#64748b", textTransform: "uppercase", fontSize: "11px", fontWeight: 600, letterSpacing: "0.5px" }}>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={600} sx={{ color: color || "#0f172a", mt: 0.5 }}>
      {value}
    </Typography>
    {subValue && (
      <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mt: 0 }}>
        {subValue}
      </Typography>
    )}
  </Box>
);

const SubscriptionTypeView = ({ plansData }) => {
  const currentPlanDetails = plansData;

  if (!currentPlanDetails) {
    return (
      <Box sx={{ width: "100%", mt: 3 }}>
        <Typography variant="body1" color="error">
          No subscription plan data found.
        </Typography>
      </Box>
    );
  }

  // Extract values
  const numLocations = currentPlanDetails.number_of_locations || 1;
  const priceDollars = (currentPlanDetails.plan_amount || 0).toFixed(2);
  const nextBillingDate = currentPlanDetails.next_due_date
    ? new Date(currentPlanDetails.next_due_date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
    : "N/A";

  // Credits
  const subGranted = currentPlanDetails.subscription_credits_granted || 0;
  const subConsumed = currentPlanDetails.subscription_credits_consumed || 0;
  const topupGranted = currentPlanDetails.topup_credits_granted || 0;
  const topupConsumed = currentPlanDetails.topup_credits_consumed || 0;
  const totalRemaining = (subGranted - subConsumed) + (topupGranted - topupConsumed);

  // Plan Validity / Frequency
  const getFrequencyLabel = (days) => {
    if (!days) return "month";
    if (days === 7) return "week";
    if (days === 1) return "day";
    if (days === 365) return "year";
    return `${days} days`;
  };
  const frequencyLabel = getFrequencyLabel(currentPlanDetails.validity_days);

  // Fallback for visual testing if data is missing (OPTIONAL: Remove if strict)
  const displayTotalCredits = currentPlanDetails.total_credits
    ? (currentPlanDetails.total_credits * numLocations).toLocaleString()
    : "0";

  return (
    <Box sx={{ width: "100%", mt: 3 }}>
      {/* HEADER / CURRENT PLAN CARD */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          p: 3,
          mb: 3,
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b" }}>
              Current Subscription
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage your plan details and billing
            </Typography>
          </Box>
          <Chip
            label={currentPlanDetails.subscription_status?.toUpperCase() || "N/A"}
            color={currentPlanDetails.subscription_status === "active" ? "success" : "default"}
            size="small"
            sx={{ fontWeight: 700, borderRadius: "6px" }}
          />
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* HORIZONTAL SUMMARY GRID */}
        <Grid container spacing={4}>
          <Grid item xs={6} md={3}>
            <InfoItem
              label="Selected Plan"
              value={currentPlanDetails.plan_name}
              subValue={`${numLocations} Location${numLocations !== 1 ? 's' : ''}`}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <InfoItem
              label="Recurring Billing"
              value={`$${(parseFloat(currentPlanDetails.plan_amount || 0) * numLocations).toFixed(2)}`}
              subValue={`$${priceDollars} × ${numLocations} / ${frequencyLabel}`}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <InfoItem
              label="Credits / Cycle"
              value={displayTotalCredits}
              subValue="Refreshes monthly"
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <InfoItem
              label="Next Billing Date"
              value={nextBillingDate}
              subValue={currentPlanDetails.cancel_at_period_end ? "Cancels after this date" : "Auto-renews"}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* DETAILED USAGE BREAKDOWN (Secondary Card) */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          p: 3,
          backgroundColor: "#f8fafc", // Slightly differentiated background
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: "#334155" }}>
          CREDIT USAGE
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={6} md={4}>
            <Box>
              <Typography variant="body2" sx={{ color: "#64748b" }}>Subscription Credits</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: "#0f172a" }}>
                  {(subGranted - subConsumed).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  / {subGranted.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "#64748b" }}>Remaining</Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={4}>
            <Box>
              <Typography variant="body2" sx={{ color: "#64748b" }}>Topup Credits</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: "#0f172a" }}>
                  {(topupGranted - topupConsumed).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  / {topupGranted.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "#64748b" }}>Remaining</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, bgcolor: "#eff6ff", borderRadius: "8px", border: "1px solid #bfdbfe", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: "#1e40af" }}>Total Available</Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: "#1d4ed8" }}>
                {totalRemaining.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SubscriptionTypeView;
